import { useState, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { isEmpty, debounce } from "lodash";
import { Helmet } from "react-helmet-async";
import {
  chevronLeftIcon,
  menuEditIcon,
  errorIcon,
  menuDeleteIcon,
  menuExportIcon,
  serviceIcon,
} from "../../assets/icons";
import { actions as dataModelActions } from "./dataModels.reducers";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import {
  deleteService,
  selectServices,
  selectSelectedDataModel,
  selectDataModelRegions,
  createService,
  selectTotalServices,
  selectServiceFilters,
} from "./index";
import { PopOver } from "../../components/atoms";
import {
  EmptyLayout,
  SearchBar,
  SuggestionBar,
  Table,
  Footer,
} from "../../components/molecules";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import { getDataModelSuggestionMessage, getRegion } from "./services.helpers";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import {
  POPOVER_BUTTONS,
  STATUS,
  TABS,
  FOOTER_CONSTANTS,
  EMPTY_STRING,
  ZERO,
  ONE,
  API_RESPONSES,
  ALERT_TIMEOUT,
  SEARCH_DEBOUNCE_TIME,
  ALERT_MESSAGES,
} from "../../constants";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import Alert from "../../components/organisms/Alert";
import {
  renderNoDataModelLayout,
  validateServiceDeletion,
} from "../../helpers";

const { DELETE, EDIT, EXPORT } = POPOVER_BUTTONS;
const { INACTIVE } = STATUS;
const { SETTINGS } = TABS;
const NAVIGATE_BACK = -1;
const SUBFLOW_NODE_ID = 84;
const { ERROR, SUCCESS } = API_RESPONSES;
const { setSelectedDataModelId, setServiceFilters } = dataModelActions;
const { setSelectedModal, setItemToBeDeleted } = modalActions;
const { setSelectedTab } = breadCrumbActions;
const { SERVICES_TITLE, SERVICES_SUBTITLE } = FOOTER_CONSTANTS;
const { DELETE_SERVICE, FETCH_SERVICES, CREATE_SERVICE } = SPINNER_NAMES;
const { DELETE_MODAL, MANAGE_SERVICE_MODAL } = MODALS;
const { INVALID_SERVICE_FILE } = ALERT_MESSAGES;

const Services = () => {
  const { dataModelId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const scrollRef = useRef();

  const allServices = useSelector(selectServices);
  const selectedDataModel = useSelector(selectSelectedDataModel);
  const dataModelRegions = useSelector(selectDataModelRegions);
  const totalServices = useSelector(selectTotalServices);
  const serviceFilters = useSelector(selectServiceFilters);
  
  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const [shouldShowSuggestionBar, setShouldShowSuggestionBar] = useState(false);
  const [alertData, setAlertData] = useState();
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);

  useEffect(() => {
    dispatch(setSelectedDataModelId(dataModelId));
  }, [dispatch, dataModelId]);

  useEffect(() => {
    scrollRef.current.scrollTop = 0;
  }, [alertData?.message]);

  useEffect(() => {
    return () => dispatch(setServiceFilters());
  }, [dispatch]);

  useEffect(() => {
    if (selectedDataModel) {
      const showSuggestionBar = Boolean(
        selectedDataModel.status === INACTIVE ||
          (dataModelRegions &&
            selectedDataModel.region !== getRegion(dataModelRegions))
      );
      setShouldShowSuggestionBar(showSuggestionBar);
      dispatch(setSelectedTab(selectedDataModel));
    }
  }, [dispatch, selectedDataModel, dataModelRegions]);

  const subflowServies = (allServices) => {
    return allServices.map(({ flow, name }) => {
      const subflowData = flow.nodes.map(({ data }) => {
        if (data.nodeId === SUBFLOW_NODE_ID) {
          return data.serviceConfig.subFlowName;
        }
        return EMPTY_STRING;
      });
      return {
        name,
        subFlow: subflowData.filter((subflow) => subflow !== EMPTY_STRING),
      };
    });
  };

  const subflow = subflowServies(allServices);

  const popOverOptions = (row) => [
    {
      name: EDIT,
      icon: menuEditIcon,
      handleOnClick: () => handleOnEditService(row),
    },
    {
      name: DELETE,
      icon: menuDeleteIcon,
      classes: "border-t border-gray-600",
      handleOnClick: () => handleDelete(row),
    },
    {
      name: EXPORT,
      icon: menuExportIcon,
      classes: "border-t border-gray-600",
      handleOnClick: () => handleExportService(row),
    },
  ];

  const nameFormatter = ({ name, isValid }) => (
    <div className="flex justify-between">
      <p>{name}</p>
      <img
        alt="error"
        className={classNames("w-4", {
          hidden: isValid,
        })}
        src={errorIcon}
      />
    </div>
  );

  const redirectFormatter = (row, subFlow) => {
    const serviceType =
      row.serviceType === "endpoint"
        ? "endpoints"
        : row.serviceType === "subflow"
        ? "subflow"
        : "backgroundTasks";
    if (!isEmpty(row[serviceType]) || serviceType === "subflow") {
      let redirectEntity = [];
      if (serviceType !== "subflow") {
        redirectEntity = row[serviceType].map(({ name, method, url }) => ({
          name:
            serviceType === "endpoints"
              ? `Method: ${method}\nURL: ${url}`
              : name,
        }));
      } else {
        let entityData = [];
        subFlow.forEach((flow) => {
          if (flow.subFlow.length > 0) {
            let subFlowData = flow.subFlow.map((subFlowName) => {
              if (subFlowName === row.name) {
                return { name: flow.name };
              }
              return { name: EMPTY_STRING };
            });
            entityData = [...entityData, ...subFlowData];
            entityData = entityData.filter((data) => data.name !== EMPTY_STRING);
          }
        });
        redirectEntity = entityData;
      }

      const entity =
        serviceType === "endpoints"
          ? "Endpoint"
          : serviceType === "subflow"
          ? "Service"
          : "Background Task";
      const pluralEntity = redirectEntity.length === ONE ? EMPTY_STRING : "s";

      return (
        redirectEntity.length && (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span>{`${redirectEntity.length} ${entity}${pluralEntity}`}</span>
            <div onClick={(e) => e.stopPropagation()}>
              <PopOver
                options={redirectEntity}
                classes="right-6"
                data={row}
                icon="info"
              />
            </div>
          </div>
        )
      );
    }
  };

  const buttonFormatter = (row) => (
    <div onClick={(e) => e.stopPropagation()}>
      <PopOver
        options={popOverOptions(row)}
        classes="right-6 -bottom-3"
        data={row}
      />
    </div>
  );

  const serviceTypeFormatter = (row) =>
    row.serviceType === "endpoint"
      ? "Endpoint Service"
      : row.serviceType === "subflow"
      ? "Subflow Service"
      : "Background Service";

  const columns = [
    {
      text: "Name",
      formatter: nameFormatter,
    },
    {
      text: "Service Type",
      formatter: serviceTypeFormatter,
    },
    {
      text: "Redirects To",
      formatter: (row) => redirectFormatter(row, subflow),
    },
    {
      width: 70,
      formatter: buttonFormatter,
    },
  ];

  const handleDeleteService = useCallback(
    (row) => {
      dispatch(deleteService(row.id)).then((res) => {
        if (res.payload) {
          dispatch(setItemToBeDeleted(null));
          dispatch(setSelectedModal(null));
          setShouldShowDeleteModal(false);
        }
      });
    },
    [dispatch]
  );

  const handleShouldShowDrawer = useCallback(
    () => dispatch(setSelectedModal(MANAGE_SERVICE_MODAL)),
    [dispatch]
  );

  const handleOnEditService = (row) =>
    navigate(`/data-models/${dataModelId}/services/${row.id}/details`);

  const handleDelete = (row) => {
    const serviceType =
      row.serviceType === "endpoint" ? "endpoints" : "backgroundTasks";
    if (isEmpty(row[serviceType])) {
      dispatch(setSelectedModal(DELETE_MODAL));
      dispatch(setItemToBeDeleted(row));
      setShouldShowDeleteModal(true);
    } else validateServiceDeletion(row, setAlertData);
  };

  const handleOnSearch = debounce(
    (name) => dispatch(setServiceFilters({ name })),
    SEARCH_DEBOUNCE_TIME
  );

  const handleExportService = useCallback((service) => {
    const jsonLink = document.createElement("a");
    jsonLink.href = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(service)
    )}`;
    jsonLink.download = `${service.name}.json`;
    jsonLink.click();
    jsonLink.remove();
  }, []);

  const handleImportService = useCallback(
    async (event) => {
      const importServiceFromFile = async (e) => {
        const serviceData = JSON.parse(e.target.result);
        const { name, description, serviceType, flow, isValid } = serviceData;
        dispatch(setServiceFilters());
        dispatch(
          createService({
            name,
            description,
            serviceType,
            dataModelId,
            flow,
            isValid,
          })
        ).then((res) => {
          if (!res.payload)
            setAlertData({ type: ERROR, message: INVALID_SERVICE_FILE });
          else
            setAlertData({
              type: SUCCESS,
              message: `Service ${name} imported.`,
            });
        });
      };

      const fileReader = new FileReader();
      const file = event.target.files[ZERO];
      event.target.value = EMPTY_STRING;
      fileReader.onloadend = importServiceFromFile;
      fileReader.readAsText(file);
    },
    [dispatch, dataModelId]
  );

  const renderServices = () =>
    !isEmpty(allServices) ? (
      <>
        <Table
          data={allServices}
          columns={columns}
          className="Bordered"
          onRowClick={handleOnEditService}
        />
        {shouldShowDeleteModal && (
          <DeleteConfirmationModal
            handleDelete={handleDeleteService}
            setShouldShowDeleteModal={setShouldShowDeleteModal}
            title="Delete Service"
            spinner={DELETE_SERVICE}
          />
        )}
      </>
    ) : (
      <EmptyLayout
        title="No Service"
        subTitle="You can get started by creating a new service."
        buttonText="Create New Service"
        image={serviceIcon}
        handleOnClick={handleShouldShowDrawer}
      />
    );
  return (
    <div className="content flex-1 flex flex-col overflow-auto">
      <Helmet>
        <title>Services | TavaJS</title>
      </Helmet>
      {alertData && (
        <div className="m-2">
          <Alert
            type={alertData.type}
            message={alertData.message}
            handleOnClose={setAlertData}
            timeout={ALERT_TIMEOUT}
          />
        </div>
      )}
      <div className="flex-col xs:flex-row flex justify-between mb-4 gap-4 sticky top-0 px-6 pt-4">
        <div className="flex flex-row">
          <button
            className="bg-gray-800 px-2 h-10 rounded-md hover:bg-gray-700/60 mr-1"
            onClick={() => navigate(NAVIGATE_BACK)}
          >
            <img src={chevronLeftIcon} width="24" alt="chevron-left-icon" />
          </button>
          {!!totalServices && (
            <SearchBar
              placeholder="Search"
              handleOnSearch={handleOnSearch}
              filters = {serviceFilters}
            />
          )}
        </div>
        {selectedDataModel && (
          <div className="flex-col xs:flex-row flex gap-4">
            <button
              type="button"
              className="btn btn-primary w-full xs:w-auto h-auto"
              onClick={handleShouldShowDrawer}
            >
              Create Service
            </button>
            <button
              type="button"
              className="btn btn-primary w-auto min-w-[80px] h-10 cursor-pointer p-0"
              disabled={isSpinnerActive}
            >
              <Spinner
                name={CREATE_SERVICE}
                setIsSpinnerActive={setIsSpinnerActive}
                persistSize
              >
                <label htmlFor="inputServiceFile" className="py-2 px-4">
                  Import
                </label>
              </Spinner>
            </button>
            <input
              type="file"
              id="inputServiceFile"
              accept="application/JSON"
              className="hidden"
              onChange={handleImportService}
            />
          </div>
        )}
      </div>
      {selectedDataModel && shouldShowSuggestionBar && (
        <SuggestionBar
          text={getDataModelSuggestionMessage(
            selectedDataModel,
            dataModelRegions
          )}
          link={SETTINGS}
          handleOnClick={() => navigate(`/data-models/${dataModelId}/settings`)}
          classes="text-red-600"
        />
      )}
      <div className="scrollbar overflow-auto h-full w-full" ref={scrollRef}>
        <div className="justify-between flex flex-col flex-1 px-6 h-full">
          <Spinner name={FETCH_SERVICES}>
            {selectedDataModel
              ? renderServices()
              : renderNoDataModelLayout(navigate)}
          </Spinner>
          {selectedDataModel && (
            <Footer title={SERVICES_TITLE} subtitle={SERVICES_SUBTITLE} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Services;
